/**
 * Друкована форма «Накладна на внутрішнє переміщення» (2026-08-11,
 * рішення власника). Довільна форма: організація, № і дата, таблиця
 * товарів зі складами джерела/отримувача ПО РЯДКАХ (0413), кількість,
 * собівартість (для проведеного), підписи «Відпустив/Прийняв».
 * Єдиний стиль друку (print-page + print-mode, кнопки друкувати/назад).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { goodsTransfers } from '@/api/goodsTransfers';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsTransferPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['goods-transfer', id],
    queryFn: () => goodsTransfers.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження накладної…</div>;
  }

  const whName = new Map((whQ.data ?? []).map((w) => [w.id, w.name]));
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const wf = (it: { warehouse_from_id?: string | null }) =>
    whName.get(it.warehouse_from_id ?? d.warehouse_from_id ?? '') ?? '—';
  const wt = (it: { warehouse_to_id?: string | null }) =>
    whName.get(it.warehouse_to_id ?? d.warehouse_to_id ?? '') ?? '—';
  const totalQty = d.items.reduce((s, it) => s + Number(it.quantity), 0);
  const totalCost = d.items.reduce((s, it) => s + Number(it.cost_amount ?? 0), 0);

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .gt-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .gt-table th, .gt-table td { border: 1px solid #000; padding: 1px 4px; }
        .gt-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.goods_transfers',
        documentId: d.id,
        filename: `Переміщення ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Накладна на внутрішнє переміщення № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table className="gt-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Найменування товару</th>
            <th style={{ width: 130 }}>Зі складу</th>
            <th style={{ width: 130 }}>На склад</th>
            <th style={{ width: 70 }}>К-сть</th>
            <th style={{ width: 45 }}>Од.</th>
            {d.posted && <th style={{ width: 90 }}>Собівартість</th>}
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}</td>
                <td>{wf(it)}</td>
                <td>{wt(it)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(it.quantity)}</td>
                <td style={{ textAlign: 'center' }}>
                  {nom?.base_uom_id ? (uomById.get(nom.base_uom_id) ?? 'шт') : 'шт'}
                </td>
                {d.posted && (
                  <td style={{ textAlign: 'right' }}>{fmtMoney(it.cost_amount ?? 0)}</td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={4} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmtQty(totalQty)}</td>
            <td></td>
            {d.posted && <td style={{ textAlign: 'right' }}>{fmtMoney(totalCost)}</td>}
          </tr>
        </tfoot>
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Всього найменувань {d.items.length}
        {d.posted && <>, на суму собівартості <strong>{fmtMoney(totalCost)} грн</strong></>}.
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Відпустив (МВО складу-джерела)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Прийняв (МВО складу-отримувача)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
